import { PaytrieAPI } from '@paytrie/client'
import {
  approveMagicLink,
  denyMagicLink,
  generateSessionId,
  getMagicLinkDetails,
  MagicLinkUnauthorizedError,
} from '@paytrie/common/auth'
import dayjs from 'dayjs'
import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  ShieldAlert,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { authService } from '@/services/auth'

const REDIRECT_COUNTDOWN_SECONDS = 10

type ViewState =
  | { kind: 'loading' }
  | { kind: 'review'; details: PaytrieAPI.MagicLinkDetails }
  | { kind: 'submitting' }
  | { kind: 'signedIn' }
  | { kind: 'denied' }
  | { kind: 'expired' }
  | { kind: 'error' }

const LoginVerifyInner = () => {
  const [view, setView] = useState<ViewState>({ kind: 'loading' })
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS)
  const [sessionId] = useState(() => generateSessionId())

  useEffect(() => {
    try {
      window.clarity?.('set', 'paytrie_session_id', sessionId)
    } catch {
      //
    }
  }, [sessionId])

  const queryParameters = useMemo(
    () => new URLSearchParams(globalThis.location.search),
    []
  )
  const email = queryParameters.get('email') || ''
  const id = queryParameters.get('id') || ''
  const isCrossTabApproval = useMemo(() => {
    try {
      return globalThis.opener != null
    } catch {
      return false
    }
  }, [])

  const handleRedirect = useCallback(() => {
    if (isCrossTabApproval) {
      try {
        globalThis.close()
      } catch (error) {
        console.warn('Could not close window:', error)
      }
      return
    }

    const redirectUrl = globalThis.location.origin
    try {
      globalThis.location.href = redirectUrl
    } catch {
      if (typeof window !== 'undefined') {
        window.location.href = redirectUrl
      }
    }
  }, [isCrossTabApproval])

  useEffect(() => {
    if (!email || !id) {
      setView({ kind: 'error' })
      return
    }

    getMagicLinkDetails({ email: email.toLowerCase(), verifier: id })
      .then((details) => setView({ kind: 'review', details }))
      .catch((error: unknown) => {
        if (error instanceof MagicLinkUnauthorizedError) {
          setView({ kind: 'expired' })
          return
        }
        setView({ kind: 'error' })
      })
  }, [email, id])

  useEffect(() => {
    if (view.kind !== 'denied' && view.kind !== 'signedIn') return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          handleRedirect()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [view.kind, handleRedirect])

  const handleApprove = async () => {
    setView({ kind: 'submitting' })
    try {
      const normalizedEmail = email.toLowerCase()
      const { accessToken } = await approveMagicLink({
        email: normalizedEmail,
        verifier: id,
        sessionId,
      })
      if (!isCrossTabApproval) {
        authService.setToken(accessToken, normalizedEmail)
      }
      setView({ kind: 'signedIn' })
    } catch (error: unknown) {
      if (error instanceof MagicLinkUnauthorizedError) {
        setView({ kind: 'expired' })
        return
      }
      setView({ kind: 'error' })
    }
  }

  const handleDeny = async () => {
    setView({ kind: 'submitting' })
    try {
      await denyMagicLink({ email: email.toLowerCase(), verifier: id })
      setView({ kind: 'denied' })
    } catch (error: unknown) {
      if (error instanceof MagicLinkUnauthorizedError) {
        setView({ kind: 'expired' })
        return
      }
      setView({ kind: 'error' })
    }
  }

  const statusIcon = () => {
    switch (view.kind) {
      case 'signedIn':
        return <CheckCircle className="w-16 h-16 text-green-600" />
      case 'denied':
        return <ShieldAlert className="w-16 h-16 text-blue-600" />
      case 'expired':
      case 'error':
        return <AlertCircle className="w-16 h-16 text-red-600" />
      case 'loading':
      case 'submitting':
      default:
        return (
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
        )
    }
  }

  if (view.kind === 'review') {
    const { details } = view
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 space-y-6">
          <div className="flex justify-center">
            <ShieldAlert className="w-16 h-16 text-blue-600" />
          </div>
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Approve sign-in?
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Someone requested a sign-in to your Paytrie account. Approve only
              if this was you.
            </p>
          </div>
          <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
            <dt className="font-medium text-gray-700 dark:text-gray-300">
              Email
            </dt>
            <dd className="text-gray-900 dark:text-white">{details.email}</dd>
            <dt className="font-medium text-gray-700 dark:text-gray-300">
              IP address
            </dt>
            <dd className="text-gray-900 dark:text-white">
              {details.requestIp}
            </dd>
            {details.requestLocation && (
              <>
                <dt className="font-medium text-gray-700 dark:text-gray-300">
                  Location
                </dt>
                <dd className="text-gray-900 dark:text-white">
                  {details.requestLocation}
                </dd>
              </>
            )}
            <dt className="font-medium text-gray-700 dark:text-gray-300">
              Requested
            </dt>
            <dd className="text-gray-900 dark:text-white">
              {dayjs(details.requestedAt).format('MMM D, YYYY HH:mm')}
            </dd>
            <dt className="font-medium text-gray-700 dark:text-gray-300">
              Expires
            </dt>
            <dd className="text-gray-900 dark:text-white">
              {dayjs(details.expiresAt).format('MMM D, YYYY HH:mm')}
            </dd>
          </dl>
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleDeny}
              variant="outline"
              className="w-full h-12 text-sm font-medium"
            >
              This wasn't me
            </Button>
            <Button
              onClick={handleApprove}
              className="w-full h-12 text-sm font-medium"
            >
              Approve sign-in
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  const [title, message] = (() => {
    switch (view.kind) {
      case 'signedIn':
        return [
          'Sign-in approved',
          isCrossTabApproval
            ? `Please return to the previous page to continue. This tab will close in ${countdown} seconds.`
            : `Sign-in complete. Redirecting in ${countdown} seconds.`,
        ]
      case 'denied':
        return [
          'Sign-in denied',
          `We blocked the sign-in request. No further action needed. Closing in ${countdown} seconds.`,
        ]
      case 'expired':
        return [
          'Link expired',
          'This verification link has expired. Please request a new login link.',
        ]
      case 'loading':
        return ['Verifying…', '']
      case 'submitting':
        return ['Processing…', '']
      case 'error':
      default:
        return ['Verification failed', 'Failed to verify. Please try again.']
    }
  })()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 text-center space-y-6">
        <div className="flex justify-center">{statusIcon()}</div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">{message}</p>
        </div>
        {view.kind === 'signedIn' && !isCrossTabApproval && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            If not redirected, please{' '}
            <button
              type="button"
              onClick={() => handleRedirect()}
              className="underline text-blue-600 hover:text-blue-700 cursor-pointer"
            >
              click here
            </button>
            .
          </p>
        )}
        {(view.kind === 'denied' ||
          view.kind === 'expired' ||
          view.kind === 'error') && (
          <Button
            onClick={() => handleRedirect()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Return to app
          </Button>
        )}
      </Card>
    </div>
  )
}

export const LoginVerify = React.memo(LoginVerifyInner)
